import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@hostfully/ui-components';
import { PARTNER_TOKEN_LENGTH } from './PartnerTokenExchange.constants';
import {
  ButtonContainer,
  FieldHint,
  InputContainer,
} from './PartnerTokenExchange.styles';
import FormFieldset from '@/components/forms/form/FormFieldset';
import InputField from '@/components/forms/InputField';

interface PartnerTokenExchangeFormProps {
  clearErrors: () => void;
  errorMessage?: string;
  isSubmitting: boolean;
}

const PartnerTokenExchangeForm = ({
  clearErrors,
  errorMessage,
  isSubmitting,
}: PartnerTokenExchangeFormProps) => {
  const { t } = useTranslation();
  const { token, redirectUri } = useWatch();

  useEffect(() => {
    clearErrors();
  }, [clearErrors, token, redirectUri]);

  return (
    <FormFieldset disabled={isSubmitting}>
      <InputContainer>
        <InputField
          label={t('pagePartnerTokenExchange.token')}
          name="token"
          description={t('pagePartnerTokenExchange.tokenHint', {
            length: PARTNER_TOKEN_LENGTH,
          })}
          autoComplete="off"
          spellCheck={false}
        />
      </InputContainer>
      <InputContainer>
        <InputField
          label={t('pagePartnerTokenExchange.redirectUri')}
          name="redirectUri"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          aria-describedby="redirectUri-hint"
        />
        {/* The design-system description only accepts plain text, while the hint needs markup. */}
        <FieldHint id="redirectUri-hint" data-testid="redirectUri-hint">
          <Trans i18nKey="pagePartnerTokenExchange.redirectUriHint" />
        </FieldHint>
      </InputContainer>
      <ButtonContainer>
        <Button
          data-testid="submit-button"
          disabled={!!errorMessage || isSubmitting}
          isLoading={isSubmitting}
          size="large"
          type="submit"
        >
          {t('pagePartnerTokenExchange.submit')}
        </Button>
      </ButtonContainer>
      {!!errorMessage && (
        <p className="error-message" data-testid="error-message">
          {errorMessage}
        </p>
      )}
    </FormFieldset>
  );
};

export default PartnerTokenExchangeForm;
